import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, AlertTriangle, Search } from 'lucide-react';

/**
 * Inventory page — feb 2026 refresh.
 *
 * Changes:
 *  • Category is now a fixed dropdown (10 options). The prefix part of each
 *    option (e.g. "PV") is also used by the backend to build per-category
 *    Item IDs like "PV-0001".
 *  • Item Name is a multi-line textarea so long descriptive names fit.
 *  • A search bar above the table filters by Item ID, Item Name, or
 *    Category. The filter runs locally — instant, no extra API calls.
 */

// Canonical category list. The first 2-3 chars before the dash are the
// prefix the backend uses to mint Item IDs. Keep these strings byte-identical
// to backend `CATEGORY_PREFIXES` keys.
const CATEGORIES = [
  'FS- Fire Stop Sealant',
  'EJ- Expansion Joint Cover',
  'RF- Raised Floor',
  'TP- Toilet Partition Phenolic',
  'PV- PVC Handrail',
  'RP- Rubber Protection',
  'SN- Stair Nosing',
  'FM- Floor Mat',
  'SD- Steel Doors',
  'OT- Other',
];

const EMPTY_FORM = {
  item_name: '',
  category: '',
  quantity: '',
  unit: '',
  unit_price: '',
  supplier: '',
  reorder_level: '',
};

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchInventory();
    const interval = setInterval(fetchInventory, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await axiosInstance.get('/inventory');
      setItems(response.data);
    } catch (error) {
      if (loading) toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axiosInstance.put(`/inventory/${editingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        const response = await axiosInstance.post('/inventory', formData);
        if (response.data.approval_status === 'pending') {
          toast.success('Item submitted for approval. Waiting for admin approval.');
        } else {
          toast.success('Item added successfully');
        }
      }
      setShowModal(false);
      resetForm();
      fetchInventory();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', '));
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axiosInstance.delete(`/inventory/${id}`);
      toast.success('Item deleted successfully');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name,
      category: item.category,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      supplier: item.supplier || '',
      reorder_level: item.reorder_level,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingItem(null);
  };

  // ── Local filter: Item ID, Item Name, Category ───────────────────────────
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const id = (it.item_number || '').toLowerCase();
      const name = (it.item_name || '').toLowerCase();
      const cat = (it.category || '').toLowerCase();
      return id.includes(q) || name.includes(q) || cat.includes(q);
    });
  }, [items, query]);

  if (loading) {
    return <div className="text-center py-12">Loading inventory...</div>;
  }

  return (
    <div data-testid="inventory-page">
      <div className="page-header">
        <h2 className="page-title">Inventory</h2>
        <button
          data-testid="add-inventory-button"
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Item
        </button>
      </div>

      {/* Search bar — Item ID / Name / Category */}
      <div className="mb-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          data-testid="inventory-search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by Item ID, Name, or Category…"
          className="form-input pl-10 w-full md:max-w-md"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            data-testid="inventory-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {filteredItems.length} of {items.length} items
        </p>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Item ID</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Unit</th>
              <th>Unit Price</th>
              <th>Total Value</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => {
                const isLowStock = item.quantity <= item.reorder_level;
                return (
                  <tr key={item.id} data-testid={`inventory-row-${item.id}`}>
                    <td className="font-mono font-semibold text-purple-700">{item.item_number || '-'}</td>
                    <td className="font-semibold whitespace-pre-wrap max-w-xs">{item.item_name}</td>
                    <td>{item.category}</td>
                    <td className={isLowStock ? 'text-red-600 font-bold' : ''}>
                      {item.quantity}
                      {isLowStock && <AlertTriangle className="w-4 h-4 inline ml-1" />}
                    </td>
                    <td>{item.unit}</td>
                    <td>{item.unit_price.toLocaleString()} SAR</td>
                    <td className="font-bold">{item.total_value.toLocaleString()} SAR</td>
                    <td>{item.supplier || '-'}</td>
                    <td>
                      <span className={`status-badge ${isLowStock ? 'overdue' : 'active'}`}>
                        {isLowStock ? 'Low Stock' : 'In Stock'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          data-testid={`edit-inventory-${item.id}`}
                          onClick={() => handleEdit(item)}
                          className="btn-icon edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-inventory-${item.id}`}
                          onClick={() => handleDelete(item.id)}
                          className="btn-icon delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="text-center py-12 text-gray-500">
                  {query
                    ? `No items match "${query}". Try another search term.`
                    : 'No inventory items found. Add your first item!'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-4xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingItem ? 'Edit Item' : 'New Item'}</h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {editingItem && editingItem.item_number && (
                <div className="form-group">
                  <label className="form-label">Item ID</label>
                  <div className="form-input bg-gray-50 font-mono font-semibold text-purple-700">
                    {editingItem.item_number}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Item Name</label>
                <textarea
                  data-testid="inventory-name-input"
                  value={formData.item_name}
                  onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                  className="form-input min-h-[80px] resize-y"
                  rows={3}
                  placeholder="Enter the full descriptive name (multi-line supported)"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    data-testid="inventory-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="form-input"
                    required
                  >
                    <option value="">— Select a category —</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {!editingItem && formData.category && (
                    <p className="text-xs text-gray-500 mt-1">
                      The next Item ID will start with{' '}
                      <span className="font-mono font-semibold text-purple-700">
                        {formData.category.split('-')[0].trim()}-XXXX
                      </span>
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input
                    data-testid="inventory-unit-input"
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="form-input"
                    placeholder="e.g., kg, ton, pieces"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    data-testid="inventory-quantity-input"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="form-input"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit Price (SAR)</label>
                  <input
                    data-testid="inventory-unit-price-input"
                    type="number"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    className="form-input"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <input
                    data-testid="inventory-supplier-input"
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Reorder Level</label>
                  <input
                    data-testid="inventory-reorder-input"
                    type="number"
                    value={formData.reorder_level}
                    onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                    className="form-input"
                    placeholder="Min quantity alert"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-inventory-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
